<script>
import { mapGetters } from 'vuex';
import LoadingState from './components/widgets/LoadingState.vue';
import NetworkNotification from './components/NetworkNotification.vue';
import UpdateBanner from './components/app/UpdateBanner.vue';
import InternalChatPanel from './components/InternalChat/InternalChatPanel.vue';
import AdminBroadcastAlert from './components/InternalChat/AdminBroadcastAlert.vue';
import PaymentPendingBanner from './components/app/PaymentPendingBanner.vue';
import PendingEmailVerificationBanner from './components/app/PendingEmailVerificationBanner.vue';
import vueActionCable from './helper/actionCable';
import { useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import WootSnackbarBox from './components/SnackbarContainer.vue';
import AccessBlocker from './components/AccessBlocker.vue';
import { setColorTheme } from './helper/themeHelper';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
import { useAccount } from 'dashboard/composables/useAccount';
import { useFontSize } from 'dashboard/composables/useFontSize';
import {
  registerSubscription,
  verifyServiceWorkerExistence,
} from './helper/pushHelper';
import ReconnectService from 'dashboard/helper/ReconnectService';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { emitter } from 'shared/helpers/mitt';

export default {
  name: 'App',

  components: {
    LoadingState,
    NetworkNotification,
    UpdateBanner,
    PaymentPendingBanner,
    WootSnackbarBox,
    PendingEmailVerificationBanner,
    InternalChatPanel,
    AdminBroadcastAlert,
    AccessBlocker,
  },
  setup() {
    const router = useRouter();
    const store = useStore();
    const { accountId } = useAccount();
    // Use the font size composable (it automatically sets up the watcher)
    const { currentFontSize } = useFontSize();
    const { uiSettings } = useUISettings();

    return {
      router,
      store,
      currentAccountId: accountId,
      currentFontSize,
      uiSettings,
    };
  },
  data() {
    const saved = JSON.parse(
      localStorage.getItem('cw-internal-bubble-pos') || 'null'
    );
    return {
      latestChatwootVersion: null,
      reconnectService: null,
      bubbleBottom: saved?.bottom ?? 24,
      bubbleRight: saved?.right ?? 24,
      bubbleLeft: saved?.left ?? null,
      isDraggingBubble: false,
      bubbleDragDistance: 0,
      bubbleStartX: 0,
      bubbleStartY: 0,
      bubbleTooltip: null,
      bubbleTooltipTimer: null,
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      isRTL: 'accounts/isRTL',
      currentUser: 'getCurrentUser',
      authUIFlags: 'getAuthUIFlags',
      accountUIFlags: 'accounts/getUIFlags',
      internalChatUnread: 'internalChat/getTotalUnreadCount',
      internalChatPanelOpen: 'internalChat/isPanelOpen',
    }),
    hideOnOnboardingView() {
      return !isOnOnboardingView(this.$route);
    },
    bubbleStyle() {
      const s = { bottom: `${this.bubbleBottom}px` };
      if (this.bubbleLeft !== null) {
        s.left = `${this.bubbleLeft}px`;
        s.right = 'auto';
      } else {
        s.right = `${this.bubbleRight}px`;
      }
      return s;
    },
  },

  watch: {
    currentAccountId: {
      immediate: true,
      handler() {
        if (this.currentAccountId) {
          this.initializeAccount();
        }
      },
    },
  },
  mounted() {
    this.initializeColorTheme();
    this.listenToThemeChanges();
    this.setLocale(
      this.uiSettings?.locale || window.chatwootConfig.selectedLocale
    );
    emitter.on('internalChatNewMessage', this.onBubbleNewMessage);
  },
  unmounted() {
    if (this.reconnectService) {
      this.reconnectService.disconnect();
    }
    emitter.off('internalChatNewMessage', this.onBubbleNewMessage);
    if (this.bubbleTooltipTimer) clearTimeout(this.bubbleTooltipTimer);
  },
  methods: {
    initializeColorTheme() {
      setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
    },
    listenToThemeChanges() {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      mql.onchange = e => setColorTheme(e.matches);
    },
    setLocale(locale) {
      this.$root.$i18n.locale = locale;
    },
    async initializeAccount() {
      await this.$store.dispatch('accounts/get');
      this.$store.dispatch('setActiveAccount', {
        accountId: this.currentAccountId,
      });
      const { locale, latest_chatwoot_version: latestChatwootVersion } =
        this.getAccount(this.currentAccountId);
      const { pubsub_token: pubsubToken } = this.currentUser || {};
      // If user locale is set, use it; otherwise use account locale
      this.setLocale(this.uiSettings?.locale || locale);
      this.latestChatwootVersion = latestChatwootVersion;
      vueActionCable.init(this.store, pubsubToken);
      this.reconnectService = new ReconnectService(this.store, this.router);
      window.reconnectService = this.reconnectService;

      // Fetch internal chat conversations for unread badge
      this.$store.dispatch('internalChat/fetchConversations');

      verifyServiceWorkerExistence(registration =>
        registration.pushManager.getSubscription().then(subscription => {
          if (subscription) {
            registerSubscription();
          }
        })
      );
    },
    onBubblePointerDown(e) {
      if (e.button !== 0) return;
      this.isDraggingBubble = false;
      this.bubbleDragDistance = 0;
      this.bubbleStartX = e.clientX;
      this.bubbleStartY = e.clientY;
      document.addEventListener('pointermove', this.onBubblePointerMove);
      document.addEventListener('pointerup', this.onBubblePointerUp);
    },
    onBubblePointerMove(e) {
      const dx = e.clientX - this.bubbleStartX;
      const dy = e.clientY - this.bubbleStartY;
      this.bubbleDragDistance = Math.sqrt(dx * dx + dy * dy);
      if (this.bubbleDragDistance > 5) {
        this.isDraggingBubble = true;
        const vh = window.innerHeight;
        const vw = window.innerWidth;
        this.bubbleBottom = Math.max(8, Math.min(vh - 56, vh - e.clientY - 24));
        this.bubbleRight = Math.max(8, Math.min(vw - 56, vw - e.clientX - 24));
        this.bubbleLeft = null;
      }
    },
    onBubblePointerUp() {
      document.removeEventListener('pointermove', this.onBubblePointerMove);
      document.removeEventListener('pointerup', this.onBubblePointerUp);
      if (this.isDraggingBubble) {
        const vw = window.innerWidth;
        const btnCenterX = vw - this.bubbleRight - 24;
        const pos = { bottom: this.bubbleBottom };
        if (btnCenterX < vw / 2) {
          this.bubbleLeft = 20;
          this.bubbleRight = 24;
          pos.left = 20;
        } else {
          this.bubbleRight = 20;
          this.bubbleLeft = null;
          pos.right = 20;
        }
        localStorage.setItem('cw-internal-bubble-pos', JSON.stringify(pos));
      }
      this.isDraggingBubble = false;
    },
    onBubbleClick() {
      if (this.bubbleDragDistance > 5) return;
      this.bubbleTooltip = null;
      this.$store.dispatch('internalChat/openPanel');
    },
    onBubbleNewMessage({ sender_name, content }) {
      if (this.internalChatPanelOpen) return;
      const preview = content && content.length > 15 ? content.substring(0, 15) + '...' : content;
      this.bubbleTooltip = { name: sender_name, preview };
      if (this.bubbleTooltipTimer) clearTimeout(this.bubbleTooltipTimer);
      this.bubbleTooltipTimer = setTimeout(() => {
        this.bubbleTooltip = null;
      }, 2000);
    },
  },
};
</script>

<template>
  <div v-if="!authUIFlags.isFetching && !accountUIFlags.isFetchingItem" id="app"
    class="flex flex-col w-full h-screen min-h-0 bg-n-background" :dir="isRTL ? 'rtl' : 'ltr'">
    <UpdateBanner :latest-chatwoot-version="latestChatwootVersion" />
    <template v-if="currentAccountId">
      <PendingEmailVerificationBanner v-if="hideOnOnboardingView" />
      <PaymentPendingBanner v-if="hideOnOnboardingView" />
    </template>
    <router-view v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>
    <WootSnackbarBox />
    <NetworkNotification />
    <AccessBlocker />
    <InternalChatPanel />
    <AdminBroadcastAlert />
    <button v-if="currentAccountId && hideOnOnboardingView"
      ref="chatBubbleBtn"
      class="fixed size-12 rounded-full bg-n-brand text-white border-none cursor-grab flex items-center justify-center shadow-lg hover:scale-105 transition-shadow z-[9990] select-none touch-none"
      :class="{ 'cursor-grabbing': isDraggingBubble }"
      :style="bubbleStyle"
      title="Chat Interno"
      @pointerdown="onBubblePointerDown"
      @click="onBubbleClick">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path
          d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"
          stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
      <span v-if="internalChatUnread > 0"
        class="absolute -top-1 -right-1 bg-red-500 text-white text-[11px] font-bold px-1.5 rounded-full min-w-[18px] text-center leading-4 border-2 border-n-background">
        {{ internalChatUnread > 9 ? '9+' : internalChatUnread }}
      </span>
      <Transition name="tooltip-pop">
        <div v-if="bubbleTooltip"
          class="absolute bottom-full mb-2 right-0 whitespace-nowrap px-3 py-1.5 rounded-lg text-xs font-semibold text-white shadow-lg pointer-events-none bg-n-brand">
          💬 <strong>{{ bubbleTooltip.name }}:</strong> {{ bubbleTooltip.preview }}
          <div class="absolute top-full right-4 border-4 border-transparent border-t-n-brand" />
        </div>
      </Transition>
    </button>
  </div>
  <LoadingState v-else />
</template>

<style lang="scss">
@import './assets/scss/app';

.v-popper--theme-tooltip .v-popper__inner {
  background: black !important;
  font-size: 0.75rem;
  padding: 4px 8px !important;
  border-radius: 6px;
  font-weight: 400;
}

.v-popper--theme-tooltip .v-popper__arrow-container {
  display: none;
}

.tooltip-pop-enter-active {
  animation: tooltip-pop-in 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
.tooltip-pop-leave-active {
  transition: all 0.2s ease-in;
}
.tooltip-pop-leave-to {
  opacity: 0;
  transform: translateY(4px) scale(0.95);
}
@keyframes tooltip-pop-in {
  0% { opacity: 0; transform: translateY(6px) scale(0.9); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}
</style>
